import React, { useMemo } from 'react';
import {
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
} from '@mui/material';

/** Parse "range(5, 20)" -> { min: 5, max: 20 } */
const parseRange = (opt) => {
  if (typeof opt !== 'string') return {};
  const m = opt.match(
    /^range\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\)$/i,
  );
  if (!m) return {};
  const min = Number(m[1]);
  const max = Number(m[2]);
  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : {};
};

const BuildingProtectionOccupancyFormFields = ({
  sectionState, // current values for this section
  specification, // array from spec (your "building_protection")
  onSectionSubmit, // (nextSectionObject) => void
  // Backwards-compat: if you still pass `data`, we'll use it:
  data, // optional legacy prop name
}) => {
  const spec = specification ?? data ?? []; // allow either prop name

  // Merge defaults from spec with current section state
  const value = useMemo(() => {
    const out = {};
    for (const field of spec) {
      const k = field.Key;
      out[k] = sectionState?.[k] ?? field.DefaultValue;
    }
    return out;
  }, [spec, sectionState]);

  const updateField = (key, nextVal) => {
    onSectionSubmit({ ...value, [key]: nextVal });
  };

  return (
    <Grid container spacing={2}>
      {spec.map((field) => {
        const { Key, DisplayName, Type, Options, DefaultValue } = field;
        const fieldValue = value[Key];

        if (Type === 'bool') {
          return (
            <Grid item xs={12} sm={12} key={Key}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!fieldValue}
                      onChange={(e) => updateField(Key, e.target.checked)}
                    />
                  }
                  label={DisplayName}
                />
              </FormGroup>
            </Grid>
          );
        }

        if (Type === 'float') {
          const { min, max } = parseRange(Options);
          return (
            <Grid item xs={12} sm={3} key={Key}>
              <TextField
                fullWidth
                variant="filled"
                label={DisplayName}
                type="number"
                value={fieldValue ?? ''} // allow '' while typing
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    updateField(Key, '');
                    return;
                  }
                  const num = Number(raw);
                  updateField(
                    Key,
                    Number.isFinite(num) ? num : (fieldValue ?? ''),
                  );
                }}
                onBlur={(e) => {
                  if (e.target.value === '') {
                    updateField(Key, DefaultValue);
                  }
                }}
                InputProps={{ disableUnderline: true }}
                inputProps={{
                  ...(min !== undefined ? { min } : {}),
                  ...(max !== undefined ? { max } : {}),
                  step: 'any',
                }}
                sx={{
                  '& .MuiFilledInput-root': {
                    borderRadius: 1,
                    overflow: 'hidden',
                  },
                }}
              />
            </Grid>
          );
        }

        return null;
      })}
    </Grid>
  );
};

export default BuildingProtectionOccupancyFormFields;
